'use client';
import React, { useState } from 'react';
import Link from 'next/link';

const productFlow = [
  { step: 'IDEA', icon: '💡', desc: 'Tell Lexigen AI your business idea' },
  { step: 'STRUCTURE', icon: '🏗️', desc: 'AI recommends the right entity type' },
  { step: 'REGISTER', icon: '📋', desc: 'AI prepares all MCA forms' },
  { step: 'TRADEMARK', icon: '™', desc: 'Protect your brand with AI search' },
  { step: 'COMPLY', icon: '✓', desc: 'Never miss a deadline again' },
  { step: 'GROW', icon: '🚀', desc: 'Scale with confidence' },
];

const features = [
  {
    icon: '🏢',
    title: 'Company Registration',
    desc: 'AI-guided 12-step workflow. SPICe+, eMOA, eAOA. Human approval before filing.',
    badge: 'MCA Ready',
  },
  {
    icon: '™',
    title: 'Trademark Intelligence',
    desc: 'Lexical, phonetic, semantic & visual similarity. Risk scoring. Class recommendation.',
    badge: 'AI Powered',
  },
  {
    icon: '₹',
    title: 'GST & MSME',
    desc: 'Eligibility check, AI form filling, auto-link with Business Passport.',
    badge: 'Auto-fill',
  },
  {
    icon: '✓',
    title: 'Compliance Center',
    desc: 'Health score, deadline alerts, AI explanations. Never miss a filing.',
    badge: 'Proactive',
  },
  {
    icon: '📄',
    title: 'Document Intelligence',
    desc: 'Upload government notices. AI extracts obligations, deadlines, and objections.',
    badge: 'OCR + AI',
  },
  {
    icon: '⊞',
    title: 'Professional Portal',
    desc: 'CAs and legal professionals manage multiple clients with AI assistance.',
    badge: 'Multi-client',
  },
];

const stats = [
  { value: '12', label: 'Step company registration' },
  { value: '45', label: 'Nice trademark classes' },
  { value: '100%', label: 'Multi-tenant isolation' },
  { value: '24/7', label: 'AI legal assistance' },
];

const suggestedQuestions = [
  'What business structure should I choose?',
  'What registrations do I need?',
  'What trademark classes do I need?',
  'What compliance is due this month?',
];

export default function HeroSection() {
  const [aiInput, setAiInput] = useState('');

  return (
    <>
      {/* Hero */}
      <section
        className="relative min-h-screen flex flex-col justify-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #0a0a1a 0%, #0d0d2b 40%, #0a1628 70%, #0a0a1a 100%)',
        }}
      >
        {/* Ambient glow */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div
            className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl opacity-20"
            style={{ width: '60vw', height: '40vw', background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)' }}
          />
          <div
            className="absolute bottom-0 right-0 rounded-full blur-3xl opacity-10"
            style={{ width: '40vw', height: '30vw', background: 'radial-gradient(circle, #6366f1 0%, transparent 70%)' }}
          />
          {/* Grid lines */}
          <div
            className="absolute inset-0 opacity-5"
            style={{
              backgroundImage: 'linear-gradient(rgba(99,102,241,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(99,102,241,0.5) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-20">
          {/* Badge */}
          <div className="flex justify-center mb-8">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-semibold tracking-wider uppercase"
              style={{ background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', color: '#818cf8' }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
              AI-POWERED LEGAL & BUSINESS REGISTRATION
            </div>
          </div>

          {/* Headline */}
          <div className="text-center mb-6">
            <h1
              className="font-bold text-white leading-[1.05] mb-6"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)', letterSpacing: '-0.03em' }}
            >
              Your AI legal team<br />
              <span style={{ background: 'linear-gradient(135deg, #818cf8, #6366f1, #4f46e5)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                for business in India.
              </span>
            </h1>
            <p className="text-[16px] sm:text-[18px] max-w-2xl mx-auto leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Start, protect and manage your business with AI-powered legal and compliance intelligence.
            </p>
          </div>

          {/* AI Principle */}
          <div className="flex justify-center mb-10">
            <div className="flex items-center gap-4 px-6 py-3 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <div className="text-center">
                <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#818cf8' }}>AI PREPARES</p>
                <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>Research · Draft · Analyze</p>
              </div>
              <div className="w-px h-8" style={{ background: 'rgba(255,255,255,0.1)' }} />
              <div className="text-center">
                <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: '#34d399' }}>HUMANS APPROVE</p>
                <p className="text-[9px]" style={{ color: 'rgba(255,255,255,0.3)' }}>Review · Sign · File</p>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              href="/onboarding"
              className="text-white font-semibold px-8 py-4 rounded-xl text-[15px] transition-all shadow-xl"
              style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)', boxShadow: '0 8px 30px rgba(99,102,241,0.4)' }}
            >
              Start Your Business →
            </Link>
            <Link
              href="/assistant"
              className="font-semibold px-8 py-4 rounded-xl text-[15px] transition-all"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' }}
            >
              Ask Lexigen AI ◎
            </Link>
          </div>

          {/* AI Quick Ask */}
          <div className="max-w-2xl mx-auto mb-16">
            <div
              className="rounded-2xl p-4"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(99,102,241,0.2)' }}
            >
              <p className="text-[11px] font-semibold tracking-wider uppercase mb-3" style={{ color: 'rgba(129,140,248,0.7)' }}>
                ◎ Ask Lexigen AI anything
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiInput}
                  onChange={e => setAiInput(e?.target?.value)}
                  placeholder="e.g. What business structure should I choose?"
                  className="flex-1 bg-transparent text-[14px] text-white placeholder-white/30 focus:outline-none"
                />
                <Link
                  href={`/assistant${aiInput ? `?q=${encodeURIComponent(aiInput)}` : ''}`}
                  className="text-white text-[13px] font-semibold px-4 py-2 rounded-xl transition-all"
                  style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)' }}
                >
                  Ask →
                </Link>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {suggestedQuestions?.map(q => (
                  <button
                    key={q}
                    onClick={() => setAiInput(q)}
                    className="text-[11px] px-3 py-1.5 rounded-lg transition-all"
                    style={{ background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.2)', color: 'rgba(255,255,255,0.5)' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {stats?.map(s => (
              <div key={s?.label} className="text-center">
                <p className="text-2xl font-bold text-white mb-1">{s?.value}</p>
                <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>{s?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Flow */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600 mb-3">THE LEXIGEN AI JOURNEY</p>
            <h2 className="text-3xl font-bold text-ink mb-4">From idea to operating business</h2>
            <p className="text-[15px] text-ink-muted max-w-xl mx-auto">AI handles research, preparation, and document work. You stay in control at every step.</p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-0">
            {productFlow?.map((item, i) => (
              <React.Fragment key={item?.step}>
                <div className="flex flex-col items-center text-center w-28 sm:w-32">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl mb-3 shadow-sm"
                    style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(99,102,241,0.05))', border: '1px solid rgba(99,102,241,0.15)' }}
                  >
                    {item?.icon}
                  </div>
                  <p className="text-[11px] font-bold tracking-wider uppercase text-indigo-600 mb-1">{item?.step}</p>
                  <p className="text-[11px] text-ink-muted leading-tight">{item?.desc}</p>
                </div>
                {i < productFlow?.length - 1 && (
                  <div className="hidden sm:block text-ink-subtle text-xl mx-1">→</div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20" style={{ background: '#f8f9ff' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600 mb-3">PLATFORM CAPABILITIES</p>
            <h2 className="text-3xl font-bold text-ink mb-4">Everything your business needs</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features?.map(f => (
              <div
                key={f?.title}
                className="bg-white rounded-2xl p-6 border border-border hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-xl"
                    style={{ background: 'linear-gradient(135deg, rgba(79,70,229,0.08), rgba(99,102,241,0.05))', border: '1px solid rgba(99,102,241,0.15)' }}
                  >
                    {f?.icon}
                  </div>
                  <span
                    className="text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full"
                    style={{ background: 'rgba(99,102,241,0.08)', color: '#6366f1' }}
                  >
                    {f?.badge}
                  </span>
                </div>
                <h3 className="font-bold text-ink text-[15px] mb-2">{f?.title}</h3>
                <p className="text-[13px] text-ink-muted leading-relaxed">{f?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Principle Section */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #0d0d2b 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-[11px] font-bold tracking-widest uppercase mb-6" style={{ color: 'rgba(129,140,248,0.7)' }}>
            THE LEXIGEN AI PRINCIPLE
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8" style={{ letterSpacing: '-0.02em' }}>
            AI does the work.<br />
            <span style={{ background: 'linear-gradient(135deg, #818cf8, #6366f1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              You stay in control.
            </span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
            <div
              className="rounded-2xl p-6 text-left"
              style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}
            >
              <p className="text-[11px] font-bold tracking-widest uppercase mb-4" style={{ color: '#818cf8' }}>LEXIGEN AI PREPARES</p>
              <div className="space-y-2">
                {['✓ Research & analysis', '✓ Document preparation', '✓ Form pre-filling', '✓ Risk assessment', '✓ Compliance monitoring', '✓ Plain-English explanations']?.map(item => (
                  <p key={item} className="text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{item}</p>
                ))}
              </div>
            </div>
            <div
              className="rounded-2xl p-6 text-left"
              style={{ background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}
            >
              <p className="text-[11px] font-bold tracking-widest uppercase mb-4" style={{ color: '#34d399' }}>HUMAN APPROVAL REQUIRED</p>
              <div className="space-y-2">
                {['✓ Legal review & sign-off', '✓ Director signatures', '✓ Professional certification', '✓ Filing authorization', '✓ Legally consequential actions', '✓ Professional judgment']?.map(item => (
                  <p key={item} className="text-[13px]" style={{ color: 'rgba(255,255,255,0.6)' }}>{item}</p>
                ))}
              </div>
            </div>
          </div>

          <Link
            href="/onboarding"
            className="inline-block text-white font-semibold px-10 py-4 rounded-xl text-[15px] transition-all shadow-xl"
            style={{ background: 'linear-gradient(135deg, #4f46e5, #6366f1)', boxShadow: '0 8px 30px rgba(99,102,241,0.4)' }}
          >
            Start Your Business with Lexigen AI →
          </Link>
        </div>
      </section>
    </>
  );
}